import './datalayer/datalayer-account-sign-in';
import DataCacheRepository from './datalayer/data-cache-repository';

function DatalayerUserId () {
	// Count to make sure that the datalayer isn't pushed twice.
	this.dataLayerPushCount = 1;
}

DatalayerUserId.ENDPOINT = '/wp-json/ns-subscribers/v1/user-entitlements';
DatalayerUserId.USER_DATA = null;

DatalayerUserId.prototype.init = function () {
	window.nsGaLogs = window.nsGaLogs || {};

	const pianoVars = document.querySelector( '#piano-variable-init' );
	/**
	 * Read the cached user entitlements data stored under KEY_USER_DATA.
	 * Institution access data is stored separately under `ns_institutions`.
	 */
	DatalayerUserId.USER_DATA = DataCacheRepository.getItem(
		DataCacheRepository.KEY_USER_DATA,
	);

	if ( ! pianoVars ) {
		return;
	}

	// @TODO: test IP auth when it is available
	if ( pianoVars.dataset.authState === 'ip-auth' || pianoVars.dataset.authState === 'educational' ) {
		// Do nothing if the user is an institution.
	} else if ( pianoVars.dataset.authState !== 'no-auth' ) {
		// Only check for the party id if the user is logged in.
		window.nsGaLogs.authState = pianoVars.dataset.authState;
		this.pushPartyId();
	} else {
		window.nsGaLogs.authState = pianoVars.dataset.authState;
		const docRoot = document.querySelector( '.article__content' );

		/**
		 * Only listen for the regwall/paywall if the user is on the article page.
		 */
		if ( docRoot ) {
			window.nsGaLogs.article = true;
			/**
			 * We should only apply the listeners if the content does not have a barrier
			 * because that is where we are targeting the regwall/paywall experiences in Piano.
			 */
			if ( 'None' === docRoot.dataset.barrier ) {
				window.nsGaLogs.barrier = docRoot.dataset.barrier;

				// Listen for the regwall to be displayed and then push the datalayer.
				window.addEventListener(
					'regWallDisplayed',
					function ( e ) {
						window.nsGaLogs.regWallDisplayed = true;
						this.pushDatalayer();
					}.bind( this ),
				);


				// Listen for the paywall to be displayed and then push the datalayer.
				window.addEventListener(
					'payWallDisplayed',
					function ( e ) {
						window.nsGaLogs.payWallDisplayed = true;
						this.pushDatalayer();
					}.bind( this ),
				);


				// Listen to see if the nonSite card has been fired. This is set up on Piano
				// to fall back to this card if no regwall/paywall is displayed.
				// https://docs.piano.io/actions/#nonsite-action
				// This listener is set up on _piano.main.script.html.twig
				window.addEventListener(
					'nonSite',
					function ( e ) {
						window.nsGaLogs.nonSite = true;
						this.pushDatalayer();
					}.bind( this ),
				);
			} else {
				window.nsGaLogs.barrier = docRoot.dataset.barrier;
				this.pushDatalayer();
			}
		} else {
			window.nsGaLogs.otherPage = true;
			this.pushDatalayer();
		}
	}
};

DatalayerUserId.prototype.pushPartyId = function () {
	if ( ! this.hasPushedDataLayer() ) {
		if ( DatalayerUserId.USER_DATA ) {
			try {
				const userDataJSON = JSON.parse( DatalayerUserId.USER_DATA.data );

				if ( userDataJSON.partyId ) {
					this.pushDatalayer( userDataJSON.partyId );
				} else {
					/**
					 * If there is no local storage with the party id, make a new request to the endpoint
					 * to get the partyID.
					 */
					this.getPartyId();
				}
			} catch ( e ) {
				this.getPartyId();
			}
		} else {
			this.getPartyId();
		}
	}
};

DatalayerUserId.prototype.pushDatalayer = function ( partyId = 0 ) {
	if ( ! this.hasPushedDataLayer() ) {
		const dataLayer = window.dataLayer || [];

		const pushObject = {
			event: 'page_data_loaded',
		};

		const institutions = window.sessionStorage.getItem( 'ns_institutions' );

		if ( institutions ) {
			const institutionsData = JSON.parse( institutions );
			pushObject.institutionName = institutionsData?.institutionName ?? '';
		}

		const cachedNsAccountSignIn = DataCacheRepository.getItem(
			DataCacheRepository.KEY_NS_ACCOUNT_SIGN_IN,
		);

		if ( partyId ) {
			pushObject.accountUserId = partyId;
		}

		/**
		 * If this window variable is added it means that the regwall is displayed in piano-custom-events.js
		 */
		if ( window.paywallDisplayed ) {
			pushObject.barrierType = window.paywallDisplayed;
		}

		window.nsGaLogs.dataLayer = pushObject;

		dataLayer.push( pushObject );

		if ( cachedNsAccountSignIn ) {
			const accountSignInObject = { event: DataCacheRepository.KEY_NS_ACCOUNT_SIGN_IN };
			accountSignInObject.service_platform = cachedNsAccountSignIn.data;
			if ( partyId ) {
				accountSignInObject.event_details = 'success';
				accountSignInObject.account_user_id = partyId;
				accountSignInObject.accountUserId = partyId;
			} else {
				const loginFormErrors = document.querySelector( '.LoginFormErrors' );
				accountSignInObject.event_details = loginFormErrors
					? loginFormErrors.getAttribute( 'data-sign-in-error' )
					: 'Error not defined';
			}
			DataCacheRepository.removeItem(
				DataCacheRepository.KEY_NS_ACCOUNT_SIGN_IN,
				DataCacheRepository.STORAGE_LOCAL,
			);
			dataLayer.push( accountSignInObject );
		}

		this.dataLayerPushCount ++;
	}
};

/**
 * Method to get the partyId from our end point and then push the datalayer when
 * it is successfully retrieved.
 */
DatalayerUserId.prototype.getPartyId = async function () {
	try {
		const response = await fetch( DatalayerUserId.ENDPOINT, {
			method: 'GET',
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
		} );

		if ( ! response.ok ) {
			throw new Error( `Response status: ${response.status}` );
		}

		const dataSet = await response.json();
		if ( dataSet.partyId ) {
			this.pushDatalayer( dataSet.partyId );
		} else {
			this.pushDatalayer();
		}
	} catch ( error ) {
		this.pushDatalayer();
	}
};

DatalayerUserId.prototype.hasPushedDataLayer = function () {
	return this.dataLayerPushCount > 1;
};

new DatalayerUserId().init();
